import React, { useEffect, useMemo, useState } from 'react';
import { useCandidatesOverviewContext } from '../../Contexts/CandidatesOverviewContext/CandidatesOverviewContext';
import checkOutOfBound from '@/Hooks/usePagination/checkOutOfBound';
import { useCandidatesTableContext } from '../../Contexts/CandidatesTableContext/CandidatesTableContext';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/Components/shadcn';
import UIStrings from '@/Utils/UIStrings';

const paginationSizeOptions = [25, 50, 100, 150, 200];

const TableNav = () => {
    const {
        pageNumber: zeroBasedPageNumbering,
        pageSize,
        totalCandidatesCount,
        handlePaginationModelChange,
    } = useCandidatesOverviewContext();
    const table = useCandidatesTableContext();
    const [pageNumber, setPageNumber] = useState(0);
    const offset = useMemo(() => (pageNumber - 1) * pageSize, [pageNumber, pageSize]);
    const currentSize = useMemo(
        () => (totalCandidatesCount < pageSize + offset ? totalCandidatesCount : pageSize + offset),
        [totalCandidatesCount, pageSize, offset],
    );
    const pageNumbers = useMemo(
        () => Array.from({ length: Math.ceil(totalCandidatesCount / pageSize) }, (_, index) => index + 1),
        [totalCandidatesCount, pageSize],
    );

    const updatePageNumber = (page: number) => {
        setPageNumber(page);
        handlePaginationModelChange({ page: page - 1, pageSize });
        table.resetRowSelection();
    };

    useEffect(() => {
        // sync with context (e.g. tab change)
        setPageNumber(zeroBasedPageNumbering + 1);
    }, [zeroBasedPageNumbering, setPageNumber]);

    useEffect(() => {
        const fixedPagination = checkOutOfBound({ limit: pageSize, offset, totalCount: totalCandidatesCount });

        if (fixedPagination) {
            updatePageNumber(fixedPagination.offset / pageSize + 1);
        }
    }, [offset, totalCandidatesCount, pageSize]);

    return (
        <div className="my-5 flex flex-wrap items-center justify-center gap-5" data-cy="tableNav">
            <Pagination className="mx-0 w-auto justify-start">
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            onClick={() => updatePageNumber(pageNumber - 1)}
                            disabled={pageNumber <= 1}
                        />
                    </PaginationItem>

                    {pageNumbers.map((page) => (
                        <PaginationItem key={page}>
                            <PaginationLink
                                isActive={pageNumber === page}
                                onClick={() => updatePageNumber(page)}
                                className="h-8 w-8 rounded-lg p-0"
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    ))}

                    <PaginationItem>
                        <PaginationNext
                            onClick={() => updatePageNumber(pageNumber + 1)}
                            disabled={pageNumber >= pageNumbers.length}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>

            <span className="text-xs">
                {UIStrings.PaginationSizeInfo(
                    totalCandidatesCount > 0 ? offset + 1 : 0,
                    currentSize,
                    totalCandidatesCount,
                )}
            </span>

            <div className="flex items-center gap-2">
                <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                        const limit = Number(value);

                        setPageNumber(1);
                        handlePaginationModelChange({ page: 0, pageSize: limit });
                    }}
                >
                    <SelectTrigger
                        id="pageSizeButton"
                        aria-label="Page size"
                        className="border-input h-9 w-fit justify-between bg-transparent shadow-none"
                    >
                        <SelectValue />
                    </SelectTrigger>

                    <SelectContent align="end" data-cy="pageSizeMenu">
                        {paginationSizeOptions.map((sizeOption) => (
                            <SelectItem key={sizeOption} value={String(sizeOption)}>
                                {sizeOption}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-xs">{UIStrings.PerPage}</span>
            </div>
        </div>
    );
};

export default TableNav;
